import { Calendar } from "lucide-react";
import { useEffect, useState, type Ref } from "react";

import { fmtDisplay } from "@/lib/date";
import { cn } from "@/lib/utils";

import { parseFlexDate } from "./parseFlexDate";

interface DateInputProps {
  label: string;
  value: Date | null;
  onCommit: (date: Date | null) => void;
  onFocus: () => void;
  onTab?: (shift: boolean) => void;
  inputRef?: Ref<HTMLInputElement>;
  active: boolean;
  id?: string;
}

/** Styled text input with floating label + flexible date parsing. */
export function DateInput({
  label,
  value,
  onCommit,
  onFocus,
  onTab,
  inputRef,
  active,
  id,
}: DateInputProps) {
  const [raw, setRaw] = useState<string>(fmtDisplay(value));
  const [invalid, setInvalid] = useState(false);

  useEffect(() => {
    setRaw(fmtDisplay(value));
    setInvalid(false);
  }, [value]);

  const tryCommit = (str: string) => {
    const d = parseFlexDate(str);
    if (d) {
      setInvalid(false);
      onCommit(d);
      return true;
    }
    return false;
  };

  return (
    <div className="relative inline-block">
      <span
        className={cn(
          "pointer-events-none absolute -top-2 left-3.5 z-10 select-none whitespace-nowrap bg-background px-1 text-[11px] font-semibold uppercase tracking-wider transition-colors",
          invalid ? "text-destructive" : active ? "text-brand" : "text-brand-light",
        )}
      >
        {label}
      </span>
      <div
        className={cn(
          "flex items-center gap-2 rounded-xl border-[1.5px] bg-background px-3.5 py-2 transition-all",
          invalid
            ? "border-destructive"
            : active
              ? "border-brand ring-[3px] ring-brand/15"
              : "border-brand-light/80",
        )}
      >
        <Calendar
          className={cn(
            "h-3.5 w-3.5 shrink-0",
            invalid ? "text-destructive" : active ? "text-brand" : "text-brand-light",
          )}
        />
        <input
          id={id}
          ref={inputRef}
          value={raw}
          onChange={(e) => {
            setRaw(e.target.value);
            setInvalid(false);
            if (e.target.value.replace(/\D/g, "").length >= 6) tryCommit(e.target.value);
          }}
          onFocus={onFocus}
          onBlur={() => {
            if (!raw.trim()) {
              onCommit(null);
              setInvalid(false);
              return;
            }
            if (!tryCommit(raw)) setInvalid(true);
          }}
          onKeyDown={(e) => {
            if (e.key === "Tab") {
              e.preventDefault();
              if (raw.trim()) tryCommit(raw);
              onTab?.(e.shiftKey);
            } else if (e.key === "Enter" && raw.trim() && !tryCommit(raw)) {
              setInvalid(true);
            }
          }}
          placeholder="MM/DD/YYYY"
          className="w-[10ch] min-w-0 border-0 bg-transparent text-sm caret-brand outline-none placeholder:text-muted-foreground/70"
        />
      </div>
      {invalid && (
        <span className="absolute left-3.5 top-full mt-0.5 text-[10px] font-medium text-destructive">
          Try: 10/1/2025 · 1-Jan-2026 · Jan 1 2026
        </span>
      )}
    </div>
  );
}
